import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import WorkoutMetaContext from './WorkoutMetaContext';
import { WorkoutDataContext, WorkoutDataActionsContext } from './WorkoutDataContext';
import WorkoutProgressContext from './WorkoutProgressContext';
import {
  loadWorkoutFromStorage,
  saveWorkoutToStorage,
  clearWorkoutStorage,
  ensureWorkoutExerciseIds,
  buildReorderIndexMap,
  remapIndexedRecord,
  remapAppliedProgression,
  createSetKey,
} from '../components/workout/liveSessionUtils';

export const DEFAULT_WORKOUT_PREFS = {
  defaultRestSeconds: 90,
  restBeepEnabled: true,
  autoStartRestTimer: true,
  autoProgressStep: 2.5,
  autoDeloadStep: 2.5,
  fullAutoProgression: false,
  showProgressionBadges: true,
  keepScreenOn: false,
  defaultRepsMin: 8,
  defaultRepsMax: 12,
  rpeTrackingEnabled: false,
};

function loadPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem('trainedbyrs_workout_prefs') || '{}');
    return { ...DEFAULT_WORKOUT_PREFS, ...saved };
  } catch {
    return DEFAULT_WORKOUT_PREFS;
  }
}

export function WorkoutSessionProvider({ children, urlOnBehalfOf, urlClientName }) {
  // ── Meta state ─────────────────────────────────────────────────
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [sourcePersonalRoutineId, setSourcePersonalRoutineId] = useState(null);
  const [sourcePersonalDayId, setSourcePersonalDayId] = useState(null);
  const [onBehalfOf, setOnBehalfOf] = useState(urlOnBehalfOf || null);
  const [clientName, setClientName] = useState(urlClientName || null);
  const [workoutSessionKey, setWorkoutSessionKey] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const [workoutPrefs, setWorkoutPrefs] = useState(loadPrefs);

  // ── Data state (keystroke-frequency) ───────────────────────────
  // NOTE: applyProgression and markAllSetsDone are intentionally LEFT in
  // WorkoutPage.js — they have richer side-effect logic (toasts, haptics,
  // PR tracking) that depends on local component state. They use the
  // setters exposed via useWorkoutDataActions(). Future tasks may move
  // them here once the side effects are decoupled.
  const [workoutData, setWorkoutData] = useState([]);
  const [lastWeights, setLastWeights] = useState({});
  const [appliedProgression, setAppliedProgression] = useState(new Map());

  // ── Progress state (action-frequency) ──────────────────────────
  const [completedSets, setCompletedSets] = useState({});
  const [incompleteSets, setIncompleteSets] = useState(new Set());
  const [showIncompleteAlert, setShowIncompleteAlert] = useState(false);
  const sessionPrMaxRef = useRef({});   // {exName: heaviest weight celebrated this session}
  const sessionPrRepsRef = useRef({});  // {exName: most reps celebrated this session (bodyweight)}
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [completedWorkout, setCompletedWorkout] = useState(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  // Bridge state — set by LiveSession's saveAndShareWorkoutMutation onSuccess,
  // consumed by WorkoutPage's routine-picker view so the prompt can appear AFTER
  // activeWorkout transitions to null. Adding here (rather than passing as a
  // callback prop) keeps LiveSession prop-free.
  const [showUpdateRoutinePrompt, setShowUpdateRoutinePrompt] = useState(false);

  // ── Hydrate from localStorage on mount (meta + data + progress) ─
  useEffect(() => {
    const stored = loadWorkoutFromStorage();
    if (stored?.activeWorkout) {
      setActiveWorkout(stored.activeWorkout);
      setSourcePersonalRoutineId(stored.sourcePersonalRoutineId || null);
      setSourcePersonalDayId(stored.sourcePersonalDayId || null);
      setOnBehalfOf(stored.onBehalfOf || null);
      setClientName(stored.clientName || null);
      setWorkoutSessionKey(stored.workoutSessionKey || '');
      setStartedAt(stored.startedAt || null);
      setWorkoutData(ensureWorkoutExerciseIds(stored.workoutData || []));
      setLastWeights(stored.lastWeights || {});
      setAppliedProgression(new Map(Object.entries(stored.appliedProgression || {})));
      setCompletedSets(stored.completedSets || {});
      setIsTimerRunning(stored.isTimerRunning || false);
    }
  }, []);

  // Persist prefs whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('trainedbyrs_workout_prefs', JSON.stringify(workoutPrefs));
    } catch {}
  }, [workoutPrefs]);

  // ── Debounced session persistence ───────────────────────────────
  // The provider OWNS persistence — WorkoutPage.js no longer writes to
  // localStorage on its own. Debounced 500ms so keystroke-rate updates
  // don't churn localStorage.
  useEffect(() => {
    if (!activeWorkout) return;
    const handle = setTimeout(() => {
      saveWorkoutToStorage({
        activeWorkout,
        sourcePersonalRoutineId,
        sourcePersonalDayId,
        onBehalfOf,
        clientName,
        workoutSessionKey,
        startedAt,
        workoutData,
        lastWeights,
        appliedProgression: Object.fromEntries(appliedProgression),
        completedSets,
        isTimerRunning,
      });
    }, 500);
    return () => clearTimeout(handle);
  }, [
    activeWorkout, sourcePersonalRoutineId, sourcePersonalDayId,
    onBehalfOf, clientName, workoutSessionKey, startedAt,
    workoutData, lastWeights, appliedProgression,
    completedSets, isTimerRunning,
  ]);

  const updatePrefs = useCallback((partial) => {
    setWorkoutPrefs((prev) => ({ ...prev, ...partial }));
  }, []);

  const endWorkout = useCallback(() => {
    setActiveWorkout(null);
    setSourcePersonalRoutineId(null);
    setSourcePersonalDayId(null);
    setOnBehalfOf(null);
    setClientName(null);
    setWorkoutSessionKey('');
    setStartedAt(null);
    setWorkoutData([]);
    setLastWeights({});
    setAppliedProgression(new Map());
    setCompletedSets({});
    setIncompleteSets(new Set());
    setShowIncompleteAlert(false);
    sessionPrMaxRef.current = {};
    sessionPrRepsRef.current = {};
    setIsTimerRunning(false);
    setCompletedWorkout(null);
    setShowCompletionDialog(false);
    clearWorkoutStorage();
  }, []);

  // ── Progress actions ───────────────────────────────────────────
  const uncompleteSet = useCallback((exIdx, setIdx) => {
    setCompletedSets((prev) => {
      const next = { ...prev };
      delete next[`${exIdx}-${setIdx}`];
      return next;
    });
  }, []);

  // Simple state-write only. The orchestrating completeSet (with haptics,
  // PR detection, rest timer auto-start, toast, unrealistic-value confirm)
  // lives in WorkoutPage.js until Task 5 extracts <LiveSession>. The local
  // completeSet calls setCompletedSets/setIncompleteSets directly via the
  // setters exposed in the progress context.
  const markSetComplete = useCallback((exIdx, setIdx) => {
    setCompletedSets((prev) => ({ ...prev, [`${exIdx}-${setIdx}`]: true }));
    setIncompleteSets((prev) => {
      if (!prev.has(`${exIdx}-${setIdx}`)) return prev;
      const n = new Set(prev);
      n.delete(`${exIdx}-${setIdx}`);
      return n;
    });
  }, []);

  const dismissCompletionDialog = useCallback(() => {
    setShowCompletionDialog(false);
    setCompletedWorkout(null);
  }, []);

  // ── Data actions (memoized) ────────────────────────────────────
  const updateSet = useCallback((exIdx, setIdx, patch) => {
    setWorkoutData((prev) => {
      if (!prev[exIdx]) return prev;
      const next = [...prev];
      const sets = [...next[exIdx].sets];
      sets[setIdx] = { ...sets[setIdx], ...patch };
      next[exIdx] = { ...next[exIdx], sets };
      return next;
    });
  }, []);

  const addSet = useCallback((exIdx) => {
    setWorkoutData((prev) => {
      if (!prev[exIdx]) return prev;
      const next = [...prev];
      const ex = next[exIdx];
      const lastSet = ex.sets[ex.sets.length - 1];
      const newSet = {
        _key: createSetKey(),
        set_number: ex.sets.length + 1,
        target_reps_min: lastSet?.target_reps_min || 8,
        target_reps_max: lastSet?.target_reps_max || 12,
        weight: lastSet?.weight || 0,
        completed_reps: 0,
        set_type: 'work',
        from_routine: false,
      };
      next[exIdx] = { ...ex, sets: [...ex.sets, newSet] };
      return next;
    });
  }, []);

  const deleteSet = useCallback((exIdx, setIdx) => {
    setWorkoutData((prev) => {
      if (!prev[exIdx]) return prev;
      const next = [...prev];
      const sets = next[exIdx].sets.filter((_, i) => i !== setIdx);
      next[exIdx] = { ...next[exIdx], sets };
      return next;
    });
  }, []);

  const addExercise = useCallback((exercise) => {
    setWorkoutData((prev) => [
      ...prev,
      ensureWorkoutExerciseIds([{ ...exercise, sets: exercise.sets || [] }])[0],
    ]);
  }, []);

  const removeExercise = useCallback((exIdx) => {
    setWorkoutData((prev) => prev.filter((_, i) => i !== exIdx));
  }, []);

  const swapExercise = useCallback((exIdx, newExercise) => {
    setWorkoutData((prev) => {
      if (!prev[exIdx]) return prev;
      const next = [...prev];
      next[exIdx] = ensureWorkoutExerciseIds([{ ...newExercise, sets: next[exIdx].sets }])[0];
      return next;
    });
  }, []);

  const reorderExercises = useCallback((fromIdx, toIdx) => {
    setWorkoutData((prev) => {
      if (fromIdx === toIdx) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      const indexMap = buildReorderIndexMap(prev, next);
      setLastWeights((lw) => remapIndexedRecord(lw, indexMap));
      setAppliedProgression((ap) => remapAppliedProgression(ap, indexMap, workoutSessionKey));
      return next;
    });
  }, [workoutSessionKey]);

  // ── Provider values ────────────────────────────────────────────
  const value = useMemo(() => ({
    activeWorkout, setActiveWorkout,
    sourcePersonalRoutineId, setSourcePersonalRoutineId,
    sourcePersonalDayId, setSourcePersonalDayId,
    onBehalfOf, setOnBehalfOf,
    clientName, setClientName,
    workoutSessionKey, setWorkoutSessionKey,
    startedAt, setStartedAt,
    workoutPrefs, updatePrefs,
    endWorkout,
  }), [
    activeWorkout, sourcePersonalRoutineId, sourcePersonalDayId,
    onBehalfOf, clientName, workoutSessionKey, startedAt, workoutPrefs,
    updatePrefs, endWorkout,
  ]);

  const dataValue = useMemo(() => ({
    workoutData,
    lastWeights,
    appliedProgression,
  }), [workoutData, lastWeights, appliedProgression]);

  const dataActions = useMemo(() => ({
    setWorkoutData,
    setLastWeights,
    setAppliedProgression,
    updateSet,
    addSet,
    deleteSet,
    addExercise,
    removeExercise,
    swapExercise,
    reorderExercises,
  }), [
    updateSet, addSet, deleteSet, addExercise, removeExercise,
    swapExercise, reorderExercises,
  ]);

  const progressValue = useMemo(() => ({
    completedSets, setCompletedSets,
    incompleteSets, setIncompleteSets,
    showIncompleteAlert, setShowIncompleteAlert,
    sessionPrMaxRef,
    sessionPrRepsRef,
    isTimerRunning, setIsTimerRunning,
    completedWorkout, setCompletedWorkout,
    showCompletionDialog, setShowCompletionDialog,
    showUpdateRoutinePrompt, setShowUpdateRoutinePrompt,
    markSetComplete, uncompleteSet, dismissCompletionDialog,
  }), [
    completedSets, incompleteSets, showIncompleteAlert,
    isTimerRunning, completedWorkout, showCompletionDialog,
    showUpdateRoutinePrompt,
    markSetComplete, uncompleteSet, dismissCompletionDialog,
  ]);

  return (
    <WorkoutMetaContext.Provider value={value}>
      <WorkoutDataContext.Provider value={dataValue}>
        <WorkoutDataActionsContext.Provider value={dataActions}>
          <WorkoutProgressContext.Provider value={progressValue}>
            {children}
          </WorkoutProgressContext.Provider>
        </WorkoutDataActionsContext.Provider>
      </WorkoutDataContext.Provider>
    </WorkoutMetaContext.Provider>
  );
}
